// import * as XLSX from "xlsx";
// import ValidateExcelFile from "./ValidateExcelFile";

// /**
//  * @param {File} file - The uploaded Excel file containing SKU data.
//  * @returns {Promise<Array>} - Resolves with the extracted data in JSON format or rejects with an error message.
//  */
// const ExtractData = (file) => {
//   //   console.log("file", file);
//   return new Promise((resolve, reject) => {
//     const fileReader = new FileReader();
//     fileReader.onload = (event) => {
//       try {
//         const data = new Uint8Array(event.target.result);
//         const workbook = XLSX.read(data, { type: "array" });

//         const firstSheetKey = Object.keys(workbook.Sheets)[0];
//         const worksheet = workbook.Sheets[firstSheetKey];

//         if (!worksheet) {
//           throw new Error("Sheet 'SKUs' not found in the Excel file.");
//         }

//         const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

//         // console.log("jsonData", jsonData);

//         const headers = jsonData[0];
//         if (!headers)
//           throw new Error(
//             "The file appears to be empty or does not contain valid headers."
//           );

//         const missingFields = ValidateExcelFile(headers);
//         if (missingFields) {
//           throw new Error(
//             `Missing required fields: ${missingFields.join(", ")}`
//           );
//         }

//         const rows = jsonData.slice(1).map((row) => {
//           const mappedRow = {};
//           headers.forEach((header, index) => {
//             mappedRow[header] = row[index];
//           });

//           return {
//             sku_id: mappedRow["MFR PART NUMBER"],
//             name: mappedRow["PRODUCT NAME"],
//             description: mappedRow["PRODUCT DESCRIPTION"],
//             vendor_name: String(mappedRow["MFR"] || "NA"),
//             uoi: mappedRow["UOI"] || "ea",
//             aspetto_price: parseFloat(mappedRow["Company Cost"] || 0),
//           };
//         });

//         resolve(rows);
//       } catch (error) {
//         console.error("Error extracting SKU data:", error.message);
//         reject(error.message);
//       }
//     };

//     fileReader.onerror = () =>
//       reject("Failed to read the file. Please try again.");
//     fileReader.readAsArrayBuffer(file);
//   });
// };

// export default ExtractData;

import * as XLSX from "xlsx";
import ValidateExcelFile from "./ValidateExcelFile";

// Crypto-based unique ID (10 chars) — called fresh for every product & kit item
const generateUniqueId10 = () => {
  const array = new Uint8Array(8);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 10);
};

const generateUniqueId = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 8; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result + Date.now().toString().slice(-4);
};

/**
 * Extracts product data from the Aspetto single-sheet import template.
 *
 * - Each top-level product gets its own unique `attachment_id`.
 * - Each kit item gets its own unique `raw_sku_id`.
 * - attachment_id is NOT added to kit items.
 * - Rows whose KIT SKU ID matches another row's SKU are nested as `productKits`.
 *
 * @param {File} file
 * @returns {Promise<Array>}
 */
const ExtractData = (file) => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const firstSheetKey = Object.keys(workbook.Sheets)[0];
        const worksheet = workbook.Sheets[firstSheetKey];

        if (!worksheet)
          throw new Error("No sheet found in the Excel file.");

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        const headers = jsonData[0];
        if (!headers?.length)
          throw new Error("The file appears to be empty or has no headers.");

        const missingFields = ValidateExcelFile(headers);
        if (missingFields)
          throw new Error(`Missing required fields: ${missingFields.join(", ")}`);

        // ── Map all rows into objects ────────────────────────────────────────
        const allRows = jsonData
          .slice(1)
          .filter((row) => row.some((cell) => cell !== ""))
          .map((row) => {
            const r = {};
            headers.forEach((h, i) => (r[h] = row[i]));

            return {
              type:              String(r["PRODUCT TYPE"] || "").trim().toLowerCase(),
              sku_id:            String(r["SKU"] || "").trim(),
              name:              String(r["PRODUCT NAME"] || ""),
              vendor_name:       String(r["VENDOR"] || ""),
              description:       String(r["DESCRIPTION (PURCHASE)"] || ""),
              description_sales: String(r["DESCRIPTION (SALES)"] || ""),
              uoi:               String(r["UOI"] || "EA"),
              aspetto_price:     r["COST"] !== "" ? parseFloat(r["COST"]) : null,
              customer_price:    r["CUSTOMER PRICE"] !== "" ? parseFloat(r["CUSTOMER PRICE"]) : null,
              kit_sku_id:        String(r["KIT SKU ID"] || "").trim(),
            };
          });

        // ── Build SKU lookup map ─────────────────────────────────────────────
        const skuMap = {};
        allRows.forEach((row) => {
          if (row.sku_id) skuMap[row.sku_id] = row;
        });

        // ── Nest rows whose KIT SKU ID matches another row's SKU ─────────────
        const nestedSkus = new Set();

        allRows.forEach((row) => {
          if (!row.kit_sku_id) return;
          const parentRow = skuMap[row.kit_sku_id];
          if (!parentRow) return;

          if (!parentRow.productKits) parentRow.productKits = [];

          // Strip kit_sku_id, add a fresh raw_sku_id per kit item
          const { kit_sku_id, ...kitItem } = row;
          parentRow.productKits.push({
            ...kitItem,
            raw_sku_id: generateUniqueId10(), // unique per kit item
          });

          nestedSkus.add(row.sku_id);
        });

        // ── Build final top-level products ───────────────────────────────────
        const products = allRows
          .filter((row) => !nestedSkus.has(row.sku_id))
          .map(({ kit_sku_id, ...rest }) => ({
            attachment_id: generateUniqueId(), // unique per top-level product
            ...rest,
            productKits: rest.productKits || [],
          }));

        resolve(products);
      } catch (error) {
        console.error("ExtractData error:", error.message);
        reject(error.message);
      }
    };

    fileReader.onerror = () =>
      reject("Failed to read the file. Please try again.");
    fileReader.readAsArrayBuffer(file);
  });
};

export default ExtractData;